import { motion } from 'framer-motion';
import { ArrowDownLeft, ArrowUpRight, Loader2, ExternalLink, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import type { TransactionRow } from '@/types/walletIntel';

function formatAmount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(3)}M`;
  if (n >= 1) return n.toFixed(5);
  return n.toExponential(3);
}

const EXPLORER: Record<string, { tx: (h: string) => string; addr: (a: string) => string; name: string }> = {
  ethereum: { tx: (h) => `https://etherscan.io/tx/${h}`, addr: (a) => `https://etherscan.io/address/${a}`, name: 'Etherscan' },
  bitcoin: { tx: (h) => `https://mempool.space/tx/${h}`, addr: (a) => `https://mempool.space/address/${a}`, name: 'mempool.space' },
  tron: { tx: (h) => `https://tronscan.org/#/transaction/${h}`, addr: (a) => `https://tronscan.org/#/address/${a}`, name: 'Tronscan' },
};

function CopyAddr({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={(e) => { e.preventDefault(); navigator.clipboard.writeText(address); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
      className="flex-shrink-0"
    >
      {copied ? <Check size={11} style={{ color: '#00C853' }} /> : <Copy size={11} style={{ color: '#3A5A7A' }} />}
    </button>
  );
}

interface Props {
  address: string;
  chain: string;
  transactions: TransactionRow[];
  hasMore: boolean;
  onLoadMore: () => void;
  isLoadingMore?: boolean;
}

export function TransactionsList({ address, chain, transactions, hasMore, onLoadMore, isLoadingMore }: Props) {
  const seed = address.toLowerCase();
  const explorer = EXPLORER[chain];

  if (transactions.length === 0) {
    return (
      <div style={{ background: '#0C1118', border: '1px dashed #182030', borderRadius: 12, padding: 28, textAlign: 'center' }}>
        <p style={{ color: '#5A7A9A', fontSize: 13 }}>No transactions found for this address.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {transactions.map((tx, i) => {
        const incoming = tx.receiver_address.toLowerCase() === seed;
        const counterparty = incoming ? tx.sender_address : tx.receiver_address;
        const dirColor = incoming ? '#00C853' : '#F0B429';
        return (
          <motion.div
            key={`${tx.transaction_hash}-${i}`}
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: Math.min(i * 0.02, 0.3) }}
            style={{ background: '#0C1118', border: '1px solid #182030', borderLeft: `3px solid ${dirColor}`, borderRadius: 12, padding: '14px 18px' }}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap mb-2.5">
              <span className="flex items-center gap-1.5" style={{ color: dirColor, fontSize: 12, fontWeight: 800, letterSpacing: '0.03em' }}>
                {incoming ? <ArrowDownLeft size={15} /> : <ArrowUpRight size={15} />}
                {incoming ? 'RECEIVED' : 'SENT'}
              </span>
              <div className="flex items-center gap-3">
                <span style={{ color: '#F2F6FC', fontFamily: 'monospace', fontSize: 15, fontWeight: 800 }}>{formatAmount(tx.amount)}</span>
                <span style={{ color: '#5A7A9A', fontSize: 11.5 }}>{new Date(tx.transaction_date).toLocaleString()}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span style={{ color: '#7C8CA6', fontSize: 11, fontWeight: 600 }}>{incoming ? 'FROM' : 'TO'}</span>
              <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{counterparty}</span>
              <CopyAddr address={counterparty} />
              {explorer && (
                <a href={explorer.addr(counterparty)} target="_blank" rel="noreferrer" style={{ color: '#00C8FF' }} title={`View on ${explorer.name}`}>
                  <ExternalLink size={11} />
                </a>
              )}
            </div>

            <div className="flex items-center gap-2 mt-2" style={{ borderTop: '1px solid #0F1520', paddingTop: 9 }}>
              <span style={{ color: '#3A5A7A', fontSize: 11, fontWeight: 600 }}>TX</span>
              <span style={{ color: '#8D96AC', fontFamily: 'monospace', fontSize: 11.5, wordBreak: 'break-all' }}>{tx.transaction_hash}</span>
              {explorer && (
                <a href={explorer.tx(tx.transaction_hash)} target="_blank" rel="noreferrer" className="flex items-center gap-1 flex-shrink-0"
                  style={{ color: '#00C8FF', fontSize: 11 }}>
                  <ExternalLink size={10} /> {explorer.name}
                </a>
              )}
              {tx.token_address && (
                <span style={{ color: '#5A7A9A', fontSize: 11, marginLeft: 'auto' }} title={tx.token_address}>
                  token transfer
                </span>
              )}
            </div>
          </motion.div>
        );
      })}
      {hasMore && (
        <div className="flex justify-center py-2">
          <motion.button
            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="px-5 py-2 rounded-lg font-bold tracking-wide flex items-center gap-2"
            style={{ background: '#0F2030', color: '#00C8FF', border: '1px solid #1E3A5F', fontSize: 12 }}
          >
            {isLoadingMore ? <Loader2 size={13} className="animate-spin" /> : null}
            LOAD MORE
          </motion.button>
        </div>
      )}
    </div>
  );
}
